import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react'
import { Formik, Form, Field, FormikErrors, useFormikContext } from 'formik'
import { IntlShape } from 'react-intl'
import * as Yup from 'yup'
import { TourPrice, PersonTypePrice, PersonType } from '../../../core/_models'
import { useQuery } from 'react-query'
import { getAll, createOne } from '../../../../../../services/common_requests'
import { toast } from 'react-toastify'

interface PricingFormProps {
  initialValues: Partial<TourPrice>
  onSubmit: (values: Partial<TourPrice>) => void
  onChange?: () => void
  intl: IntlShape
  tourId: string
  isSubmitting?: boolean
}

interface FormValues extends Partial<TourPrice> {
  personTypePrices: PersonTypePrice[]
  pricingMarketingNote?: string
}

interface PersonTypePriceError {
  personType?: string
  price?: string
}

const validationSchema = Yup.object().shape({
  pricingMode: Yup.string().required('Required'),
  priceTTC: Yup.number()
    .required('Required')
    .min(0.01, 'Price must be greater than 0'),
  priceHT: Yup.number()
    .required('Required')
    .min(0.01, 'Price must be greater than 0'),
  personTypePrices: Yup.array().when('pricingMode', {
    is: 'PER_PERSON',
    then: Yup.array().of(
      Yup.object().shape({
        personTypeId: Yup.string().required('Required'),
        price: Yup.number()
          .required('Required')
          .min(0.01, 'Price must be greater than 0')
      })
    ),
    otherwise: Yup.array()
  }),
  pricingMarketingNote: Yup.string().max(2000, 'Maximum 2000 characters')
})

const PricingForm = forwardRef<{ handleSubmit: () => void }, PricingFormProps>(
  ({ initialValues, onSubmit, onChange, intl, tourId, isSubmitting }, ref) => {
    // Fetch person types
    const { data: personTypesData } = useQuery(
      'personTypes',
      () => getAll(null, '/person-types'),
      {
        staleTime: Infinity,
      }
    )

    const personTypes = (personTypesData?.data || []) as PersonType[]
    let formikRef: any = null

    // Tax state - always store as decimal (e.g., 0.19 for 19%)
    const [taxRate, setTaxRate] = useState(0.19)
    const [taxMode, setTaxMode] = useState<'percentage' | 'decimal'>('percentage')
    const [manualPriceTTC, setManualPriceTTC] = useState(false)
    const [manualPriceHT, setManualPriceHT] = useState(false)

    // Helper to calculate priceTTC from priceHT
    const calculatePriceTTC = (priceHT: number, rate: number) => {
      if (taxMode === 'decimal') {
        // For decimal mode: TTC = HT + tax (direct addition)
        return Number((priceHT + rate).toFixed(2))
      } else {
        // For percentage mode: TTC = HT * (1 + rate)
        return Number((priceHT * (1 + rate)).toFixed(2))
      }
    }

    // Helper to calculate priceHT from priceTTC
    const calculatePriceHT = (priceTTC: number, rate: number) => {
      if (taxMode === 'decimal') {
        // For decimal mode: HT = TTC - tax (direct subtraction)
        return Number((priceTTC - rate).toFixed(2))
      } else {
        // For percentage mode: HT = TTC / (1 + rate)
        return Number((priceTTC / (1 + rate)).toFixed(2))
      }
    }

    useImperativeHandle(ref, () => ({
      handleSubmit: () => {
        if (formikRef) {
          formikRef.handleSubmit()
        }
      },
      resetForm: () => {
        if (formikRef) {
          formikRef.resetForm()
        }
      }
    }))

    // Helper to ensure value is number or undefined
    const getNumberValue = (val: string | number | undefined | null): number | undefined => {
      if (typeof val === 'number') return val
      if (typeof val === 'string') return val === '' ? undefined : Number(val)
      return undefined
    }

    // Helper to get the display value for the tax input
    const getTaxInputValue = () => {
      return taxMode === 'percentage' ? (taxRate * 100) : taxRate
    }

    // Helper to handle tax mode switch
    const handleTaxModeSwitch = (mode: 'percentage' | 'decimal', formik: any) => {
      if (mode === taxMode) return
      
      setTaxMode(mode)
      setTaxRate(0)
      
      // Recalculate priceTTC based on current priceHT with new tax rate (0)
      const currentPriceHT = Number(formik.values.priceHT) || 0
      const newPriceTTC = calculatePriceTTC(currentPriceHT, 0)
      formik.setFieldValue('priceTTC', newPriceTTC)
    }

    // Helper to handle tax input change
    const handleTaxInputChange = (e: React.ChangeEvent<HTMLInputElement>, formik: any) => {
      const val = parseFloat(e.target.value)
      let newTaxRate: number
      
      if (taxMode === 'percentage') {
        newTaxRate = isNaN(val) ? 0 : val / 100
      } else {
        newTaxRate = isNaN(val) ? 0 : val
      }
      
      setTaxRate(newTaxRate)
      
      // Only update priceTTC based on current priceHT
      const currentPriceHT = Number(formik.values.priceHT) || 0
      const newPriceTTC = calculatePriceTTC(currentPriceHT, newTaxRate)
      formik.setFieldValue('priceTTC', newPriceTTC)
    }

    return (
      <Formik<FormValues>
        initialValues={{
          ...initialValues,
          personTypePrices: (initialValues.personTypePrices || []).map(ptp => ({
            personTypeId: ptp.personTypeId || '',
            price: ptp.price ?? 0,
            priceHT: ptp.priceHT ?? 0
          })),
          priceHT: typeof initialValues.priceHT === 'number' ? initialValues.priceHT : undefined,
          priceTTC: typeof initialValues.priceTTC === 'number' ? initialValues.priceTTC : undefined,
          pricingMode: initialValues.pricingMode || 'PER_PERSON',
          pricingMarketingNote: initialValues.pricingMarketingNote ?? ''
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          const priceTTC = Number(values.priceTTC)
          const priceHT = Number(values.priceHT)
          const personTypePrices = values.pricingMode === 'PER_PERSON' 
            ? values.personTypePrices.map(price => ({
                personTypeId: price.personTypeId,
                price: Number(price.price) ?? 0,
                priceHT: Number(price.priceHT) ?? 0
              }))
            : []
          onSubmit({
            ...values,
            priceTTC,
            priceHT,
            personTypePrices,
            pricingMarketingNote:
              typeof values.pricingMarketingNote === 'string'
                ? values.pricingMarketingNote.trim().slice(0, 2000)
                : ''
          })
        }}
        enableReinitialize
        validateOnChange={true}
        validateOnBlur={true}
        innerRef={(formik) => {
          formikRef = formik
        }}
      >
        {({ values, errors, touched, setFieldValue, handleChange, handleBlur }) => {
          // Handle priceTTC change - calculate priceHT
          const handlePriceTTCChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            setManualPriceTTC(true)
            setManualPriceHT(false)
            const val = parseFloat(e.target.value)
            const priceTTC = isNaN(val) ? 0 : val
            setFieldValue('priceTTC', priceTTC)
            // Do NOT update priceHT here
            if (onChange) onChange()
          }

          // Handle priceHT change - calculate priceTTC
          const handlePriceHTChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            setManualPriceHT(true)
            setManualPriceTTC(false)
            const val = parseFloat(e.target.value)
            const priceHT = isNaN(val) ? 0 : val
            setFieldValue('priceHT', priceHT)
            // Calculate priceTTC from priceHT
            const newPriceTTC = calculatePriceTTC(priceHT, taxRate)
            setFieldValue('priceTTC', newPriceTTC)
            if (onChange) onChange()
          }

          // Form validation state is handled by Formik

          const personTypePriceErrors = errors.personTypePrices as PersonTypePriceError[] | undefined
          const personTypePriceTouched = touched.personTypePrices as { [key: string]: boolean }[] | undefined

          const handleFieldChange = (e: React.ChangeEvent<any>, callback?: (value: any) => void) => {
            handleChange(e)
            if (callback) {
              callback(e.target.value)
            }
            if (onChange) {
              onChange()
            }
          }

          return (
            <Form className='form'>
              {/* First row: Price (HT) and Tax Rate - 50/50 split */}
              <div className='row mb-6'>
                <div className='col-md-6'>
                  <label className='form-label required'>
                    {intl.formatMessage({ id: 'COLUMNS.PRICE' })} (HT)
                  </label>
                  <input
                    type='number'
                    name='priceHT'
                    min='0.1'
                    step='0.1'
                    className={`form-control form-control-solid ${
                      errors.priceHT && touched.priceHT ? 'is-invalid' : ''
                    }`}
                    value={getNumberValue(values.priceHT) ?? ''}
                    onChange={handlePriceHTChange}
                    onBlur={handleBlur}
                  />
                  {errors.priceHT && touched.priceHT && (
                    <div className='invalid-feedback'>{errors.priceHT}</div>
                  )}
                </div>
                <div className='col-md-6'>
                  <label className='form-label required'>
                    {intl.formatMessage({ id: 'TOUR.PRICING.TAX_RATE' })}
                  </label>
                  <div className='d-flex align-items-center'>
                    <input
                      type='number'
                      min='0'
                      step='0.1'
                      className='form-control form-control-solid me-2'
                      value={getTaxInputValue()}
                      onChange={(e) => handleTaxInputChange(e, { values, setFieldValue })}
                    />
                    <div className='form-check form-check-inline ms-2'>
                      <input
                        className='form-check-input'
                        type='radio'
                        name='taxMode'
                        id='taxModePercentage'
                        checked={taxMode === 'percentage'}
                        onChange={() => handleTaxModeSwitch('percentage', { values, setFieldValue })}
                      />
                      <label className='form-check-label' htmlFor='taxModePercentage'>%</label>
                    </div>
                    <div className='form-check form-check-inline ms-2'>
                      <input
                        className='form-check-input'
                        type='radio'
                        name='taxMode'
                        id='taxModeDecimal'
                        checked={taxMode === 'decimal'}
                        onChange={() => handleTaxModeSwitch('decimal', { values, setFieldValue })}
                      />
                      <label className='form-check-label' htmlFor='taxModeDecimal'>fixed</label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Second row: Sale Price (TTC) */}
              <div className='row mb-6'>
                <div className='col-md-6'>
                  <label className='form-label required'>
                    {intl.formatMessage({ id: 'COLUMNS.TOTAL_PRICE' })} (TTC)
                  </label>
                  <input
                    type='number'
                    name='priceTTC'
                    min='0.1'
                    step='0.1'
                    className={`form-control form-control-solid ${
                      errors.priceTTC && touched.priceTTC ? 'is-invalid' : ''
                    }`}
                    value={getNumberValue(values.priceTTC) ?? ''}
                    onChange={handlePriceTTCChange}
                    onBlur={handleBlur}
                    disabled
                  />
                  {errors.priceTTC && touched.priceTTC && (
                    <div className='invalid-feedback'>{errors.priceTTC}</div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <div className='col-12'>
                  <label className='form-label'>
                    {intl.formatMessage({
                      id: 'TOUR.PRICING.MARKETING_NOTE_LABEL',
                      defaultMessage: 'Public pricing message (optional)',
                    })}
                  </label>
                  <textarea
                    name='pricingMarketingNote'
                    rows={4}
                    maxLength={2000}
                    className={`form-control form-control-solid ${
                      errors.pricingMarketingNote && touched.pricingMarketingNote ? 'is-invalid' : ''
                    }`}
                    placeholder={intl.formatMessage({
                      id: 'TOUR.PRICING.MARKETING_NOTE_PLACEHOLDER',
                      defaultMessage: 'E.g.: 5th traveller free — information only; does not change the calculated total.',
                    })}
                    value={values.pricingMarketingNote ?? ''}
                    onChange={(e) => {
                      setFieldValue('pricingMarketingNote', e.target.value)
                      if (onChange) onChange()
                    }}
                    onBlur={handleBlur}
                  />
                  <div className='form-text'>
                    {(values.pricingMarketingNote ?? '').length}/2000 —{' '}
                    {intl.formatMessage({
                      id: 'TOUR.PRICING.MARKETING_NOTE_HELP',
                      defaultMessage: 'Shown on the booking page next to pricing. Does not alter price calculation.',
                    })}
                  </div>
                  {errors.pricingMarketingNote && touched.pricingMarketingNote && (
                    <div className='invalid-feedback d-block'>{errors.pricingMarketingNote}</div>
                  )}
                </div>
              </div>

              <div className='card mb-6'>
                <div className='card-header'>
                  <div className='card-title d-flex justify-content-between align-items-center w-100'>
                    <h3 className='mb-0'>
                      {intl.formatMessage({ id: 'TOUR.PRICING.PERSON_TYPE_PRICES' })}
                    </h3>
                    <div className='form-check form-switch'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        role='switch'
                        id='pricingMode'
                        checked={values.pricingMode === 'PER_PERSON'}
                        onChange={(e) => {
                          handleFieldChange(e, () => {
                            setFieldValue('pricingMode', e.target.checked ? 'PER_PERSON' : 'PER_GROUP')
                            if (!e.target.checked) {
                              setFieldValue('personTypePrices', [])
                            }
                          })
                        }}
                      />
                    </div>
                  </div>
                </div>
                {values.pricingMode === 'PER_PERSON' && (
                  <div className='card-body'>
                    {/* Show message when no person type prices are defined */}
                    {(!values.personTypePrices || values.personTypePrices.length === 0) ? (
                      <div className='text-center py-10'>
                        <div className='mb-5'>
                          <i className='ki-duotone ki-user-tick fs-3x text-muted'>
                            <span className='path1'></span>
                            <span className='path2'></span>
                            <span className='path3'></span>
                          </i>
                        </div>
                        <h3 className='fs-5 fw-bold text-gray-900 mb-1'>
                          {intl.formatMessage({ id: 'TOUR.PRICING.NO_PERSON_TYPE_PRICES' }, { defaultMessage: 'No person type prices defined' })}
                        </h3>
                        <div className='fs-7 fw-semibold text-gray-600 mb-5'>
                          {intl.formatMessage({ 
                            id: 'TOUR.PRICING.NO_PERSON_TYPE_PRICES_DESCRIPTION', 
                            defaultMessage: 'Add person type specific pricing for different age groups' 
                          })}
                        </div>
                        <button
                          type='button'
                          className='btn btn-sm btn-primary'
                          onClick={() => {
                            // Get first unused person type
                            const currentPersonTypes = values.personTypePrices?.map(ptp => ptp.personTypeId) || []
                            const availablePersonType = personTypes.find(pt => !currentPersonTypes.includes(pt._id))
                            
                            if (!availablePersonType) {
                              toast.warning(intl.formatMessage({ 
                                id: 'TOUR.PRICING.ALL_PERSON_TYPES_USED',
                                defaultMessage: 'All available person types have been added'
                              }))
                              return
                            }

                            const newPrice: PersonTypePrice = {
                              personTypeId: availablePersonType._id,
                              price: 0,
                              priceHT: 0
                            }
                            setFieldValue('personTypePrices', [newPrice])
                            if (onChange) onChange()
                          }}
                        >
                          <i className='ki-duotone ki-plus fs-2'></i>
                          {intl.formatMessage({ id: 'TOUR.PRICING.ADD_PERSON_TYPE_PRICE' })}
                        </button>
                      </div>
                    ) : (
                      <>
                        {/* Display configured person type prices */}
                        <div className='mb-5'>
                          {(values.personTypePrices || []).map((ptp, index) => {
                            // Get next available person type for this entry
                            const currentPersonTypes = values.personTypePrices
                              ?.map(p => p.personTypeId)
                              .filter((_, i) => i !== index) || []
                            const availablePersonTypes = personTypes.filter(pt => !currentPersonTypes.includes(pt._id))
                            
                            // If no person type is selected, auto-select the first available one
                            if (!ptp.personTypeId && availablePersonTypes.length > 0) {
                              setTimeout(() => {
                                setFieldValue(`personTypePrices.${index}.personTypeId`, availablePersonTypes[0]._id)
                              }, 0)
                            }

                            return (
                              <div key={index} className='row align-items-center mb-4'>
                                <div className='col-md-6'>
                                  <label className='form-label required'>
                                    {intl.formatMessage({ id: 'TOUR.PRICING.PERSON_TYPE' })}
                                  </label>
                                  <select
                                    className={`form-select ${
                                      personTypePriceErrors?.[index]?.personType && personTypePriceTouched?.[index]?.personType
                                        ? 'is-invalid'
                                        : ''
                                    }`}
                                    value={ptp.personTypeId || ''}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                      handleFieldChange(e, () => {
                                        const selectedType = personTypes.find(pt => pt._id === e.target.value)
                                        setFieldValue(`personTypePrices.${index}.personTypeId`, selectedType?._id || '')
                                      })
                                    }}
                                    onBlur={handleBlur}
                                    disabled={!availablePersonTypes.length && !ptp.personTypeId}
                                  >
                                    <option value=''>{intl.formatMessage({ id: 'COMMON.SELECT' })}</option>
                                    {[
                                      ...(ptp.personTypeId ? [personTypes.find(pt => pt._id === ptp.personTypeId)] : []),
                                      ...availablePersonTypes.filter(pt => pt._id !== ptp.personTypeId)
                                    ].filter((pt): pt is PersonType => pt !== undefined).map((pt) => (
                                      <option key={pt._id} value={pt._id}>
                                        {pt.name}
                                      </option>
                                    ))}
                                  </select>
                                  {personTypePriceErrors?.[index]?.personType && 
                                   personTypePriceTouched?.[index]?.personType && (
                                    <div className='invalid-feedback'>
                                      {personTypePriceErrors[index].personType}
                                    </div>
                                  )}
                                </div>
                                <div className='col-md-4'>
                                  <label className='form-label required'>
                                    {intl.formatMessage({ id: 'COLUMNS.PRICE' })} (€)
                                  </label>
                                  <Field
                                    name={`personTypePrices.${index}.price`}
                                    type='number'
                                    min='0.01'
                                    step='0.01'
                                    className={`form-control ${
                                      personTypePriceErrors?.[index]?.price && 
                                      personTypePriceTouched?.[index]?.price 
                                        ? 'is-invalid' 
                                        : ''
                                    }`}
                                    value={ptp.price ?? 0}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                      const price = parseFloat(e.target.value)
                                      handleFieldChange(e, () => {
                                        setFieldValue(`personTypePrices.${index}.price`, price)
                                        setFieldValue(`personTypePrices.${index}.priceHT`, calculatePriceHT(price, taxRate))
                                      })
                                    }}
                                    onBlur={handleBlur}
                                  />
                                  {personTypePriceErrors?.[index]?.price && 
                                   personTypePriceTouched?.[index]?.price && (
                                    <div className='invalid-feedback'>
                                      {personTypePriceErrors[index].price}
                                    </div>
                                  )}
                                </div>
                                <div className='col-md-2 d-flex align-items-end mt-8'>
                                  <button
                                    type='button'
                                    className='btn btn-icon btn-light-danger'
                                    onClick={() => {
                                      const newPrices = [...(values.personTypePrices || [])]
                                      newPrices.splice(index, 1)
                                      setFieldValue('personTypePrices', newPrices)
                                      if (onChange) onChange()
                                    }}
                                    title={intl.formatMessage({ id: 'ACTION.DELETE' })}
                                  >
                                    <i className='fas fa-trash-alt'></i>
                                  </button>
                                </div>
                              </div>
                            )
                          })}
                        </div>

                        {/* Add button - only show if there are unused person types */}
                        {values.personTypePrices.length < personTypes.length && (
                          <div className='d-flex justify-content-center'>
                            <button
                              type='button'
                              className='btn btn-light-primary'
                              onClick={() => {
                                // Get next available person type
                                const currentPersonTypes = values.personTypePrices?.map(ptp => ptp.personTypeId) || []
                                const availablePersonType = personTypes.find(pt => !currentPersonTypes.includes(pt._id))
                                
                                if (!availablePersonType) return

                                const newPrice: PersonTypePrice = {
                                  personTypeId: availablePersonType._id,
                                  price: 0,
                                  priceHT: 0
                                }
                                setFieldValue('personTypePrices', [...(values.personTypePrices || []), newPrice])
                                if (onChange) onChange()
                              }}
                            >
                              <i className='ki-duotone ki-plus fs-2'></i>
                              {intl.formatMessage({ id: 'TOUR.PRICING.ADD_ANOTHER_PERSON_TYPE' })}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
                {/* Show message when Person Type pricing is disabled */}
                {values.pricingMode !== 'PER_PERSON' && (
                  <div className='card-body'>
                    <div className='text-center py-5'>
                      <div className='text-muted fs-6'>
                        <i className='ki-duotone ki-information-5 fs-2 text-gray-500 me-2'>
                          <span className='path1'></span>
                          <span className='path2'></span>
                          <span className='path3'></span>
                        </i>
                        {intl.formatMessage({ 
                          id: 'TOUR.PRICING.GROUP_PRICING_MODE', 
                          defaultMessage: 'Currently using group pricing mode. Enable person type pricing to set different prices for age groups.' 
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Form>
          )
        }}
      </Formik>
    )
  }
)

export default PricingForm